import { useMemo, useState } from "react";
import {
  useGetCustomersQuery,
  useDeleteCustomerMutation,
} from "../../../../redux/api";
import type { Customer } from "../../../../redux/api";
import {
  IconPlus,
  IconSearch,
  IconEye,
  IconEdit,
  IconTrash,
} from "../../../../components/icons";
import { CustomerFormModal } from "./customer";
import { EntityOverview } from "../../../../components/EntityOverview";
import { OverviewFieldList } from "../../../../components/OverviewFieldList";
import { statusTone as overviewStatusTone } from "../../../../utils/status";
import { RowMenu } from "../../../../components/RowMenu";

const COLUMN_COUNT = 6;

const statusTone = (status?: string | null) =>
  (status ?? "").toLowerCase() === "active" ? "badge--success" : "badge--warning";

// Inventory → Customers (list page).
const Customers = () => {
  const {
    data: customers = [],
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetCustomersQuery();

  const [deleteCustomer] = useDeleteCustomerMutation();

  const [search, setSearch] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const openDetail = (id: string | null) => {
    setEditingId(id);
    setModalOpen(true);
  };

  const [viewingId, setViewingId] = useState<string | null>(null);
  const viewing = customers.find((c) => c.id === viewingId) ?? null;


  const handleDelete = async (customer: Customer) => {
    if (!window.confirm(`Delete "${customer.name}"?`)) return;
    try {
      await deleteCustomer(customer.id).unwrap();
    } catch (err) {
      window.alert(
        (err as { message?: string })?.message ?? "Failed to delete customer.",
      );
    }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((c: Customer) =>
      [c.name, c.code, c.email, c.phone, c.status].some((field) =>
        (field ?? "").toLowerCase().includes(q),
      ),
    );
  }, [customers, search]);

  const errorMessage = error
    ? (error as { message?: string }).message ?? "Failed to load customers"
    : null;

  if (viewing) {
    return (
      <>
        <EntityOverview
          backLabel="Customers"
          onBack={() => setViewingId(null)}
          title={viewing.name}
          id={viewing.id}
          status={{ label: viewing.status, tone: overviewStatusTone(viewing.status) }}
          onEdit={() => openDetail(viewing.id)}
          onDelete={() => {
            void handleDelete(viewing).then(() => setViewingId(null));
          }}
        >
          <OverviewFieldList
            fields={[
              { label: "Code", value: viewing.code },
              { label: "Email", value: viewing.email },
              { label: "Phone", value: viewing.phone },
              { label: "GSTIN", value: viewing.gstin },
              { label: "Contact person", value: viewing.contactPerson },
              { label: "Address line 1", value: viewing.addressLine1, full: true },
              { label: "Address line 2", value: viewing.addressLine2, full: true },
              { label: "City", value: viewing.city },
              { label: "State", value: viewing.state },
              { label: "Country", value: viewing.country },
              { label: "Postal code", value: viewing.postalCode },
              { label: "Credit limit", value: viewing.creditLimit },
            ]}
          />
        </EntityOverview>
        <CustomerFormModal open={modalOpen} id={editingId} onClose={() => setModalOpen(false)} />
      </>
    );
  }

  return (
    <div className="dashboard">
      <section className="panel">
        <div className="panel__head">
          <div>
            <h2>Customers</h2>
            <p className="panel__sub">
              Showing {visible.length} of {customers.length}
            </p>
          </div>
          <div className="toolbar">
            <div className="search">
              <span className="search__icon">
                <IconSearch />
              </span>
              <input
                type="search"
                placeholder="Search customers…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="btn"
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {isFetching ? "Refreshing…" : "Refresh"}
            </button>
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => openDetail(null)}
            >
              <IconPlus /> New customer
            </button>
          </div>
        </div>

        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Email</th>
                <th>Phone</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    Loading customers…
                  </td>
                </tr>
              ) : errorMessage ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    <div className="table-error">
                      <span>{errorMessage}</span>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => refetch()}
                      >
                        Try again
                      </button>
                    </div>
                  </td>
                </tr>
              ) : visible.length === 0 ? (
                <tr>
                  <td className="data-table__empty" colSpan={COLUMN_COUNT}>
                    {customers.length === 0
                      ? "No customers yet."
                      : "No customers match your search."}
                  </td>
                </tr>
              ) : (
                visible.map((customer: Customer) => (
                  <tr key={customer.id}>
                    <td className="cell-title">
                      <button
                        type="button"
                        className="btn btn--ghost"
                        onClick={() => setViewingId(customer.id)}
                      >
                        {customer.name}
                      </button>
                    </td>
                    <td>
                      {customer.code ? (
                        <span className="pill pill--neutral">{customer.code}</span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td className="cell-date">{customer.email ?? "—"}</td>
                    <td>{customer.phone ?? "—"}</td>
                    <td>
                      <span className={`badge ${statusTone(customer.status)}`}>
                        <span className="badge__dot" />
                        {customer.status}
                      </span>
                    </td>
                    <td>
                      <RowMenu
                        items={[
                          {
                            label: "View",
                            icon: <IconEye size={16} />,
                            onSelect: () => setViewingId(customer.id),
                          },
                          {
                            label: "Edit",
                            icon: <IconEdit size={16} />,
                            onSelect: () => openDetail(customer.id),
                          },
                          {
                            label: "Delete",
                            icon: <IconTrash size={16} />,
                            onSelect: () => handleDelete(customer),
                            danger: true,
                          },
                        ]}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>


      <CustomerFormModal open={modalOpen} id={editingId} onClose={() => setModalOpen(false)} />
    </div>
  );
};

export default Customers;
